import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
  Autocomplete
} from '@mui/material';
import {
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { Item } from '../models/Item';
import { getCategoriesForShop, getDefaultCategoryForShop } from '../utils/categories';

const BulkImportDialog = ({ open, onClose, shopName, onImport }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [items, setItems] = useState([]);
  const [errors, setErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

        // Parse the data
        const parsedItems = parseExcelData(jsonData);
        setItems(parsedItems);
        validateItems(parsedItems);
        setActiveTab(1); // Switch to preview tab
      } catch (error) {
        alert('Error reading file: ' + error.message);
        console.error('Error reading file:', error);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const parseExcelData = (rows) => {
    if (rows.length < 2) return [];

    // Assume first row is header
    const headers = rows[0].map(h => (h || '').toString().toLowerCase().trim());

    // Find column indices
    const nameIdx = findColumnIndex(headers, ['name', 'item name', 'product name']);
    const brandIdx = findColumnIndex(headers, ['brand', 'manufacturer']);
    const categoryIdx = findColumnIndex(headers, ['category', 'type']);
    const descriptionIdx = findColumnIndex(headers, ['description', 'desc', 'notes']);
    const quantityIdx = findColumnIndex(headers, ['quantity', 'qty', 'stock', 'in stock']);
    const sellingPriceIdx = findColumnIndex(headers, ['selling price', 'price', 'sell price', 'retail price']);
    const originalCostIdx = findColumnIndex(headers, ['original cost', 'cost', 'purchase price', 'buy price']);

    const parsedItems = [];
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row || row.every(cell => !cell)) continue; // Skip empty rows

      const item = new Item({
        shopName,
        name: (row[nameIdx] || '').toString().trim() || 'New Item',
        brand: (row[brandIdx] || '').toString().trim() || '',
        category: (row[categoryIdx] || '').toString().trim() || getDefaultCategoryForShop(shopName),
        description: (row[descriptionIdx] || '').toString().trim() || '',
        quantity: parseFloat(row[quantityIdx]) || 0,
        sellingPrice: parseFloat(row[sellingPriceIdx]) || 0,
        originalCost: parseFloat(row[originalCostIdx]) || 0
      });

      parsedItems.push(item);
    }

    return parsedItems;
  };

  const findColumnIndex = (headers, possibleNames) => {
    for (const name of possibleNames) {
      const idx = headers.findIndex(h => h.includes(name));
      if (idx !== -1) return idx;
    }
    return -1;
  };

  const validateItems = (itemsToValidate) => {
    const validationErrors = [];
    itemsToValidate.forEach((item, index) => {
      if (!item.name || item.name.trim() === '') {
        validationErrors.push(`Row ${index + 1}: Name is required`);
      }
      if (item.quantity < 0) {
        validationErrors.push(`Row ${index + 1}: Quantity cannot be negative`);
      }
      if (item.sellingPrice < 0) {
        validationErrors.push(`Row ${index + 1}: Selling price cannot be negative`);
      }
      if (item.originalCost < 0) {
        validationErrors.push(`Row ${index + 1}: Original cost cannot be negative`);
      }
      if (!item.category || item.category.trim() === '') {
        validationErrors.push(`Row ${index + 1}: Category is required`);
      }
    });
    setErrors(validationErrors);
    return validationErrors.length === 0;
  };

  const handleAddRow = () => {
    const newItem = new Item({
      shopName,
      name: '',
      brand: '',
      category: getDefaultCategoryForShop(shopName),
      description: '',
      quantity: 0,
      sellingPrice: 0,
      originalCost: 0
    });
    setItems([...items, newItem]);
  };

  const handleDeleteRow = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    validateItems(newItems);
  };

  const handleCellChange = (index, field, value) => {
    const newItems = [...items];
    const item = newItems[index];

    if (field === 'quantity' || field === 'sellingPrice' || field === 'originalCost') {
      item[field] = parseFloat(value) || 0;
    } else {
      item[field] = value;
    }

    setItems(newItems);
    validateItems(newItems);
  };

  const handleImport = async () => {
    if (items.length === 0) {
      alert('No items to import');
      return;
    }

    if (!validateItems(items)) {
      alert('Please fix validation errors before importing');
      return;
    }

    setImporting(true);
    try {
      await onImport(items);
      handleClose();
    } catch (error) {
      alert('Error importing items: ' + error.message);
      console.error('Error importing items:', error);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setItems([]);
    setErrors([]);
    setActiveTab(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const downloadTemplate = () => {
    const templateData = [
      ['Name', 'Brand', 'Category', 'Description', 'Quantity', 'Selling Price', 'Original Cost'],
      ['Sample Tire', 'Michelin', 'Tire', '225/65R17', '10', '5000', '3500'],
      ['Motor Oil', 'Castrol', 'Oil', '5W-30 Synthetic', '20', '800', '500']
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Items');
    XLSX.writeFile(wb, 'bulk_import_template.xlsx');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Bulk Import Items</Typography>
          <Button
            startIcon={<DownloadIcon />}
            onClick={downloadTemplate}
            size="small"
            variant="outlined"
          >
            Download Template
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Upload File" />
          <Tab label={`Preview & Edit (${items.length} items)`} disabled={items.length === 0} />
        </Tabs>

        {activeTab === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              size="large"
            >
              Upload Excel/CSV File
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Supported formats: .xlsx, .xls, .csv
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              First row should contain headers: Name, Brand, Category, Description, Quantity, Selling Price, Original Cost
            </Typography>
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body1">
                {items.length} item(s) ready to import
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddRow}
                size="small"
              >
                Add Row
              </Button>
            </Box>

            {errors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Validation Errors:</Typography>
                {errors.map((error, idx) => (
                  <Typography key={idx} variant="body2">• {error}</Typography>
                ))}
              </Alert>
            )}

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', width: 80 }}>
                      Actions
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A' }}>Brand</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', width: 120 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', width: 100 }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', width: 120 }}>Selling Price</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#3A3A3A', width: 120 }}>Original Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteRow(index)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.name}
                          onChange={(e) => handleCellChange(index, 'name', e.target.value)}
                          fullWidth
                          required
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.brand}
                          onChange={(e) => handleCellChange(index, 'brand', e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          freeSolo
                          size="small"
                          options={getCategoriesForShop(shopName)}
                          value={item.category}
                          onChange={(e, newValue) => handleCellChange(index, 'category', newValue || '')}
                          onInputChange={(e, newInputValue) => handleCellChange(index, 'category', newInputValue || '')}
                          renderInput={(params) => (
                            <TextField {...params} size="small" placeholder="Category" />
                          )}
                          disableClearable
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.description}
                          onChange={(e) => handleCellChange(index, 'description', e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleCellChange(index, 'quantity', e.target.value)}
                          inputProps={{ min: 0 }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={item.sellingPrice}
                          onChange={(e) => handleCellChange(index, 'sellingPrice', e.target.value)}
                          inputProps={{ min: 0, step: 0.01 }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={item.originalCost}
                          onChange={(e) => handleCellChange(index, 'originalCost', e.target.value)}
                          inputProps={{ min: 0, step: 0.01 }}
                          fullWidth
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {importing && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              Importing items...
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={items.length === 0 || errors.length > 0 || importing}
        >
          Import {items.length} Item(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkImportDialog;

